import React, {useEffect, useRef, useState} from 'react';
import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {useTranslation} from 'react-i18next';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  y: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  series?: string;
  grid?: {
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
  };
  color?: string | {}; // バーグラフの色
  lineColor?: string; // 折れ線グラフの色
  thresholdColor?: string; // 80%閾値線の色
  height?: string;
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  xList: any;
  chartToggleStatus?: boolean; // チャート切替ボタン表示フラグ
  isParetoMode?: boolean; // パレートモード表示フラグ
  onChartToggle?: () => void; // チャート切替処理
  timeUnit?: number; // 0: 検査ごと, 1: 1時間ごと, 2: 1日ごと, 3: 1ヶ月ごと
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
}

/**
 * LFCChartsParetoYMD
 * パレート図表示コンポーネント
 * @param props
 * @returns
 */
const LFCChartsParetoYMD = React.memo((props: Props) => {
  const {t} = useTranslation();
  const renderFlgRef = useRef(false);

  type DsRow = {
    source?: Array<{category: string; value: number}> | [];
    transform?: any;
  };
  const [dataset, setDataset] = useState<DsRow[]>([]);
  const [series, setSeries] = useState([]);

  // シリーズごとの合計値と全体の合計値を保持するstate
  const [seriesTotal, setSeriesTotal] = useState<{[key: string]: number}>({});
  const [totalSum, setTotalSum] = useState<number>(0);
  // ソート済みのシリーズ名を保持するstate
  const [sortedSeries, setSortedSeries] = useState<string[]>([]);
  // パレート図用のエクスポートデータを保持するstate
  const [paretoExportData, setParetoExportData] = useState<any[]>([]);

  // 特殊なカテゴリ名を定義（原点表示用）- コンポーネントレベルで定義
  const originCategory = '__origin__';

  // カテゴリごとに異なる色を定義（コンポーネントレベルに移動）
  const colorPalette = [
    '#a8fd74',
    '#77cbff',
    '#689bea',
    '#d48265',
    '#91c7ae',
    '#749f83',
    '#ca8622',
    '#bda29a',
    '#6e7074',
    '#546570',
    '#c4ccd3',
    '#f05b72',
    '#ef5b9c',
    '#f47920',
    '#905a3d',
    '#fab27b',
    '#2a5caa',
    '#444693',
    '#726930',
    '#b2d235'
  ];

  useEffect(() => {
    // Reset the render flag when isParetoMode changes to ensure re-rendering
    if (props.isParetoMode === undefined || props.isParetoMode === true) {
      renderFlgRef.current = true;
    }

    // データソースクリア
    if (props.source.length === 0) {
      setSeries([]);
      setDataset([]);
      setSeriesTotal({});
      setTotalSum(0);
      setParetoExportData([]); // Clear pareto export data
      return;
    }

    // データソース生成
    // パレート図用に降順ソートしたデータを作成
    const l_ds: DsRow[] = [];
    const l_series: any = [];

    if (props.series) {
      // シリーズ指定あり
      let strSeries: string = props.series;

      // 各シリーズごとの合計を計算
      const calculatedSeriesTotal: {[key: string]: number} = {};

      // 各シリーズごとの合計を計算
      props.source.forEach((item: any) => {
        const seriesName = item[strSeries];
        const value = Number(item[props.y.dsColumn]) || 0;

        if (!calculatedSeriesTotal[seriesName]) {
          calculatedSeriesTotal[seriesName] = 0;
        }
        calculatedSeriesTotal[seriesName] += value;
      });

      // stateに保存（ツールチップで使用）
      setSeriesTotal(calculatedSeriesTotal);

      // シリーズ名と合計値のペアを作成して降順ソート
      const calculatedSortedSeries = Object.entries(calculatedSeriesTotal)
        .sort((a, b) => b[1] - a[1])
        .map(entry => entry[0]);

      // ステートに保存
      setSortedSeries(calculatedSortedSeries);

      // 合計値を計算
      const calculatedTotalSum = Object.values(calculatedSeriesTotal).reduce(
        (sum, value) => sum + value,
        0
      );

      // stateに保存（ツールチップで使用）
      setTotalSum(calculatedTotalSum);

      // パレート図用のエクスポートデータを生成
      if (props.isParetoMode === true) {
        const newParetoData = [];
        let cumSum = 0;

        // ソート済みのカテゴリに対して、パーセンテージと累積パーセンテージを計算
        calculatedSortedSeries.forEach(category => {
          const value = calculatedSeriesTotal[category];
          const ratio = (value / calculatedTotalSum) * 100;
          cumSum += value;
          const cumulativePercentage = (cumSum / calculatedTotalSum) * 100;

          newParetoData.push({
            category: category,
            value: value,
            percentage: ratio.toFixed(1),
            cumulative_percentage: cumulativePercentage.toFixed(1)
          });
        });

        setParetoExportData(newParetoData);
      }

      // 累積パーセンテージデータ（ライン用）
      let cumSum = 0;
      const lineData = [];
      // 80%を超える点のインデックスを計算用
      let index80 = -1;

      // 原点(0,0)を追加する
      lineData.push({
        category: originCategory, // 特殊な名前で原点を識別
        value: 0 // 値は0
      });

      // 各カテゴリに対する累積パーセンテージを計算し、
      // 中間ポイントのみにデータを追加（カテゴリポイントは削除）
      calculatedSortedSeries.forEach((category, index) => {
        cumSum += calculatedSeriesTotal[category];
        const percentValue = (cumSum / calculatedTotalSum) * 100;

        // 実カテゴリポイントは追加しない

        // 中間カテゴリポイントのみ追加 - シンボル表示用
        lineData.push({
          category: `__mid_${index}`,
          value: percentValue,
          // 中間ポイントには明示的にシンボルを表示する設定を追加
          itemStyle: {
            symbol: 'circle',
            symbolSize: 6
          }
        });

        // 80%を超える点のインデックスを同時に計算
        if (index80 === -1 && percentValue >= 80) {
          index80 = index;
        }
      });

      // データセットの設定（累積パーセンテージライン用）
      l_ds.push({
        source: lineData
      });

      // 各カテゴリごとに個別のデータセットとシリーズを作成（別々の色を適用するため）
      calculatedSortedSeries.forEach((category, index) => {
        // カテゴリごとのデータを作成
        const singleBarData = [
          {
            category: category,
            value: calculatedSeriesTotal[category]
          }
        ];

        // データセットを追加
        l_ds.push({
          source: singleBarData
        });

        // インデックスに基づいて色を選択（循環させる）
        const colorIndex = index % colorPalette.length;
        const color = colorPalette[colorIndex];

        // シリーズを追加
        l_series.push({
          type: 'bar',
          name: category,
          encode: {
            x: 'category',
            y: 'value'
          },
          datasetIndex: index + 1,
          // バーの幅と位置の調整 - 中間ポイントまでカバーするように設定
          barCategoryGap: '-50%', // カテゴリ間の隙間を調整して位置をセンターからずらす
          barGap: '-100%', // 同一カテゴリ内の棒が完全に重なるようにする
          barWidth: '200%', // バーの幅を広げて中間ポイントまでカバー
          z: 2, // 折れ線グラフよりも背面に表示する
          // 各カテゴリごとに異なる色を設定
          itemStyle: {
            color: color, // カテゴリごとに異なる色を使用
            opacity: 1 // すべてのカテゴリを同じ不透明度に
          }
        });
      });

      // 累積パーセンテージの塗りつぶしエリア（area）を先に追加（背面に表示）
      l_series.push({
        type: 'line',
        name: t('累積パーセンテージエリア'),
        encode: {
          x: 'category',
          y: 'value'
        },
        datasetIndex: 0, // 最初のデータセット（累積パーセンテージデータ）
        yAxisIndex: 1,
        showSymbol: false, // シンボルを非表示
        z: 1, // 最背面に表示（棒グラフz: 2より背面）
        // 折れ線グラフのスタイル設定
        smooth: false,
        clip: true, // グラフ領域でクリップ

        // エリア表示のみで線は非表示
        lineStyle: {
          width: 0,
          opacity: 0
        },

        // エリアスタイル設定（塗りつぶしエリアのみ表示）
        areaStyle: {
          opacity: 0.2,
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                // 指定された折れ線の色を使用（なければデフォルト色）
                color: props.lineColor
                  ? `${props.lineColor}4D` // 透明度30%（4D）を追加
                  : 'rgba(24, 144, 255, 0.3)' // デフォルト色
              },
              {
                offset: 0.8, // 80%位置
                // 指定された折れ線の色を使用（なければデフォルト色）
                color: props.lineColor
                  ? `${props.lineColor}4D` // 透明度30%（4D）を追加
                  : 'rgba(24, 144, 255, 0.3)' // デフォルト色
              },
              {
                offset: 0.801, // 80%位置のすぐ下
                // 指定された折れ線の色を使用（なければデフォルト色）- 上部と同じ色を使用
                color: props.lineColor
                  ? `${props.lineColor}4D` // 透明度30%（4D）を追加
                  : 'rgba(24, 144, 255, 0.3)' // デフォルト色
              }
            ]
          }
        },
        tooltip: {
          show: false // ツールチップを非表示
        },
        connectNulls: true
      });

      // 累積パーセンテージのラインシリーズを追加（最前面に表示）
      l_series.push({
        type: 'line',
        name: t('累積パーセンテージ'),
        encode: {
          x: 'category',
          y: 'value'
        },
        datasetIndex: 0, // 最初のデータセット（累積パーセンテージデータ）
        yAxisIndex: 1,
        symbol: 'circle',
        symbolSize: 6,
        symbolPosition: 'start\n',
        itemStyle: {
          // 折れ線グラフの色を指定（プロパティがあればそれを使用、なければデフォルト色）
          color: props.lineColor || '#ffad6c'
        },
        symbolOffset: [0, 0],
        // シンボルの表示を制御
        symbolFilter: function (dataIndex, data) {
          const category = data.category;
          // 原点と実カテゴリではシンボルを非表示、中間カテゴリでのみ表示
          return category !== originCategory && category.toString().startsWith('__mid_');
        },
        z: 10, // 最前面に表示（棒グラフz: 2よりも前面）
        // 折れ線グラフのスタイル設定
        smooth: false,
        clip: true, // グラフ領域でクリップ

        // 線のスタイル設定（確実に実線になるようにする）
        lineStyle: {
          // 折れ線グラフの色を指定（プロパティがあればそれを使用、なければデフォルト色）
          color: props.lineColor || '#ff7f1c',
          width: 2,
          type: 'solid'
        },

        // エリアスタイル無効化（別のシリーズで表示）
        areaStyle: null,
        connectNulls: true,

        // 80%ラインの追加（縦線と横線）
        markLine: {
          silent: true,
          symbol: ['none', 'circle'], // 終点に円形マーカーを表示
          symbolSize: [0, 10], // 終点のマーカーサイズを設定
          tooltip: {
            show: false // ツールチップを明示的に非表示に設定
          },
          lineStyle: {
            // 80%閾値線の色を指定（プロパティがあればそれを使用、なければデフォルト色）
            color: props.thresholdColor || '#ffdb15', // 閾値用のデフォルト色
            type: 'dashed',
            width: 1.5
          },
          emphasis: {
            lineStyle: {
              width: 2 // ホバー時に線を太くする
            }
          },
          data:
            calculatedSortedSeries.length > 0
              ? [
                  {
                    // 横線（水平線）- 80%の位置に
                    yAxis: 80, // Y軸の80%位置に水平線
                    symbol: ['none', 'none'], // 明示的に両端の矢印を非表示
                    symbolSize: [0, 0], // シンボルのサイズを0に
                    label: {
                      show: false // 80%ラベルを非表示に設定
                    }
                  },
                  // 以下が変更部分 - 上部で計算済みのindex80を使用
                  {
                    // 累積パーセンテージが80%を超える点に縦線を配置
                    // 上部で計算済みのindex80を使用して、正確な位置に縦線を表示
                    // 中間ポイントを参照するように変更
                    xAxis:
                      index80 >= 0
                        ? `__mid_${index80}`
                        : `__mid_${calculatedSortedSeries.length - 1}`,
                    symbol: ['none', 'none'],
                    symbolSize: [0, 0],
                    label: {
                      show: false // 縦線のラベルも非表示に設定
                    }
                  }
                ]
              : [] // データが空の場合は空の配列を設定
        }
      });
    }

    // 80%閾値ラインを凡例に表示するためのダミーシリーズを追加
    // 実際の閾値線はmarkLineによって描画されるが、このダミーシリーズが凡例に表示される
    if (sortedSeries.length > 0) {
      l_series.push({
        type: 'line',
        name: t('80％閾値ライン'), // 凡例に表示する名前
        showSymbol: false,
        // 実際にはデータを表示しないがダミーポイントが必要
        data: [],
        lineStyle: {
          // 80%閾値線の色を指定
          color: props.thresholdColor || '#ffdb15',
          type: 'dashed',
          width: 1.5
        },
        // 実際には表示されないように透明度を0に設定
        itemStyle: {
          opacity: 0
        },
        // ツールチップも表示しない
        tooltip: {
          show: false
        }
      });
    }

    setSeries(l_series);
    setDataset(l_ds);
  }, [props.source, props.series, props.y.dsColumn, props.isParetoMode]);

  return (
    <LFCBaseCharts
      option={{
        title: {
          text: props.title
        },
        grid: props.grid
          ? props.grid
          : {
              top: '40',
              left: '60',
              right: '60',
              bottom: '160' // 下部の余白を増やして凡例とX軸ラベルのスペースを確保
            },
        legend: {
          type: 'scroll',
          bottom: 0, // 下部に余裕を持たせる
          itemWidth: 10,
          textStyle: {
            fontSize: 11 // 凡例テキストのサイズを調整
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            // 累積パーセンテージライン
            if (params.seriesName === t('累積パーセンテージ')) {
              const category = params.data.category;
              const value = params.data.value;

              // 基本ツールチップ内容
              let tooltipContent = '';
              // カテゴリ名が __mid_ で始まる場合はカテゴリ名を表示しない
              if (!category || !category.toString().startsWith('__mid_')) {
                tooltipContent = `<div style="font-weight: bold; margin-bottom: 5px;">${category}</div>`;
              }
              tooltipContent += `<span style="color: #ff7f1c;">${params.seriesName}：${value.toFixed(1)}%</span>`;

              // 80%前後（75%～85%の範囲）の場合、重要カテゴリ情報を追加
              if (value >= 75 && value <= 85) {
                // データポイントのインデックスを取得
                const dataIndex = params.dataIndex;
                if (dataIndex !== undefined) {
                  // 累積値が80%を超えるカテゴリ数を計算
                  const itemCount = dataIndex;
                  const totalCount = sortedSeries.length;
                  const percentage = ((itemCount / totalCount) * 100).toFixed(1);

                  // ツールチップに重要カテゴリ情報を追加
                  tooltipContent += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ccc;">`;
                  tooltipContent += `<span style="color: #ff6600; font-weight: bold;">カテゴリ情報</span><br/>`;
                  tooltipContent += `上位${itemCount}項目で不良全体の約80%を占めています<br/>`;
                  tooltipContent += `(全${totalCount}項目中${percentage}%)`;
                  tooltipContent += `</div>`;
                }
              }

              return tooltipContent;
            }

            // 棒グラフ（各カテゴリ）
            if (params.seriesType === 'bar') {
              const category = params.data.category;
              const value = params.data.value;
              // 構成比を計算
              const ratio = totalSum > 0 ? (value / totalSum) * 100 : 0;

              let categoryLabel = '';

              // シリーズタイプに応じてラベルを設定
              if (props.series === 'map') {
                categoryLabel = t('部位');
              } else if (props.series === 'work') {
                categoryLabel = t('機種');
              } else if (props.series === 'job_type_name') {
                categoryLabel = t('検査手法');
              }

              return `${categoryLabel}：${category}<br/>
                ${t('不良数')}：<span style="color: #FF0000">${value}</span><br/>
                ${t('構成比')}：<span style="color: #FF0000">${ratio.toFixed(1)}%</span>`;
            }

            // マークラインのツールチップは非表示に設定済み
            if (params.componentType === 'markLine') {
              return '';
            }

            return '';
          }
        },
        // dataZoomを有効化（x軸ズームをミッドポイント間隔で設定、CloudDistanceの倍のステップ）
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: 0,
            // ミッドポイント間隔でのズーム（CloudDistanceの倍のステップ）
            zoomOnMouseWheel: 'ctrl',
            moveOnMouseMove: 'ctrl',
            // 最小と最大のズーム範囲を設定
            minValueSpan: 4, // 最小4つの項目を表示（2つのカテゴリ + 2つのミッドポイント）
            maxValueSpan: undefined // 最大ズーム範囲は制限なし
          }
        ],
        color: props.color ? props.color : null,
        dataset: dataset,
        xAxis: [
          {
            type: 'category',
            // x軸の範囲を調整して最後のミッドポイントまで表示するように設定
            min: originCategory,
            max: sortedSeries.length > 0 ? `__mid_${sortedSeries.length - 1}` : originCategory,
            // スケール調整を自動ではなく固定に設定して、表示範囲が正確に制御されるようにする
            scale: false,
            axisLabel: {
              rotate: '45',
              fontSize: 10,
              interval: 0, // すべてのラベルを表示
              margin: 30, // 軸とラベルの間隔を広げる
              width: 120, // ラベルの最大幅
              overflow: 'truncate', // 長いラベルは省略
              align: 'center', // ラベルをカテゴリの中央に配置
              rich: {
                a: {
                  lineHeight: 20
                }
              },
              // 中間カテゴリの場合はラベルを非表示にし、原点の場合は "0" を表示
              formatter: function (value) {
                if (value.toString().startsWith('__mid_')) {
                  return ''; // 中間カテゴリは非表示
                } else if (value === '__origin__') {
                  return ''; // 原点は "0" と表示
                } else {
                  return value; // その他の値はそのまま表示
                }
              }
            },
            axisTick: {
              alignWithLabel: true, // 目盛りをラベルの中央に合わせる
              length: 0, // 目盛りの長さを増やして視覚的に位置を明確にする
              lineStyle: {
                width: 2 // 目盛り線を太くする
              }
            },
            axisLine: {
              onZero: false // 軸線をゼロ位置から切り離し、適切な位置に配置
            },
            boundaryGap: false, // バーチャートの境界のギャップをなくし、最初のバーをY軸に接するようにする
            // カテゴリをソートされた順序で表示し、中間ポイントを追加
            data: (() => {
              // 原点と実カテゴリと中間ポイントを含むx軸データの作成
              const fullAxisData = [];
              fullAxisData.push(originCategory); // 原点を追加

              sortedSeries.forEach((category, index) => {
                // 実カテゴリを追加（棒グラフ表示のために必要）
                fullAxisData.push(category);

                // 中間ポイントを追加（折れ線グラフの接続ポイント用）
                fullAxisData.push(`__mid_${index}`);
              });

              return fullAxisData;
            })()
          }
        ],
        yAxis: [
          {
            type: props.y.type,
            name: '', // Y軸名を非表示化
            min: 0, // 最小値を0に設定
            max: totalSum || 'dataMax', // 不良数合計を最大値に設定
            scale: false, // スケール自動調整を無効化して固定範囲にする
            axisLabel: {
              fontSize: 10,
              color: '#FF0000' // 不良数のY軸ラベルを赤色に
            },
            nameTextStyle: {
              color: '#FF0000' // 軸名も赤色に
            }
          },
          {
            type: 'value',
            name: t(''),
            min: 0,
            max: 100,
            axisLabel: {
              formatter: '{value}%',
              fontSize: 10
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#ccc'
              }
            }
            // 横線は累積パーセンテージのシリーズのmarkLineに移動
          }
        ],
        series: series
      }}
      exportData={
        props.isParetoMode && paretoExportData.length > 0 ? paretoExportData : props.exportData
      }
      exportFields={props.exportFields}
      exportFilename={props.exportFilename}
      height={props.height}
      chartToggleStatus={props.chartToggleStatus}
      isParetoMode={props.isParetoMode}
      onChartToggle={props.onChartToggle}
      totalFailureCount={totalSum} // 不良ワーク数合計を渡す
      zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
      zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
      zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
      zoomStatus={props.zoomChartId === props.zoomedChartId}
    />
  );
});

export default LFCChartsParetoYMD;
